import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { MINUS_ONE } from '@/constants/common';
import {
  getAnnotators,
  getAnnotatorTargets,
  getAnnotatorPresets,
  createAnnotatorPreset,
  updateAnnotatorPreset,
  deleteAnnotatorPreset,
  getAllAnnotationJobs,
  cancelAnnotationJob,
} from './annotators.thunks';
import type { AnnotatorsState } from './annotators.types';

export const getAnnotatorsReducer = (builder: ActionReducerMapBuilder<AnnotatorsState>): void => {
  builder.addCase(getAnnotators.pending, state => {
    state.annotators.loading = 'pending';
  });
  builder.addCase(getAnnotators.fulfilled, (state, action) => {
    state.annotators.data = action.payload;
    state.annotators.loading = 'succeeded';
  });
  builder.addCase(getAnnotators.rejected, (state, action) => {
    state.annotators.loading = 'failed';
    state.annotators.error = action.error as Error;
  });
};

export const getAnnotatorTargetsReducer = (
  builder: ActionReducerMapBuilder<AnnotatorsState>,
): void => {
  builder.addCase(getAnnotatorTargets.pending, state => {
    state.targets.loading = 'pending';
  });
  builder.addCase(getAnnotatorTargets.fulfilled, (state, action) => {
    state.targets.data = action.payload;
    state.targets.loading = 'succeeded';
  });
  builder.addCase(getAnnotatorTargets.rejected, (state, action) => {
    state.targets.loading = 'failed';
    state.targets.error = action.error as Error;
  });
};

export const getAnnotatorPresetsReducer = (
  builder: ActionReducerMapBuilder<AnnotatorsState>,
): void => {
  builder.addCase(getAnnotatorPresets.pending, state => {
    state.presets.loading = 'pending';
  });
  builder.addCase(getAnnotatorPresets.fulfilled, (state, action) => {
    state.presets.data = action.payload;
    state.presets.loading = 'succeeded';
  });
  builder.addCase(getAnnotatorPresets.rejected, (state, action) => {
    state.presets.loading = 'failed';
    state.presets.error = action.error as Error;
  });
};

export const createAnnotatorPresetReducer = (
  builder: ActionReducerMapBuilder<AnnotatorsState>,
): void => {
  builder.addCase(createAnnotatorPreset.fulfilled, (state, action) => {
    if (state.presets.data) {
      state.presets.data.push(action.payload);
    }
  });
};

export const updateAnnotatorPresetReducer = (
  builder: ActionReducerMapBuilder<AnnotatorsState>,
): void => {
  builder.addCase(updateAnnotatorPreset.fulfilled, (state, action) => {
    if (state.presets.data) {
      const index = state.presets.data.findIndex(p => p.id === action.payload.id);
      if (index !== MINUS_ONE) {
        state.presets.data[index] = action.payload;
      }
    }
  });
};

export const deleteAnnotatorPresetReducer = (
  builder: ActionReducerMapBuilder<AnnotatorsState>,
): void => {
  builder.addCase(deleteAnnotatorPreset.fulfilled, (state, action) => {
    if (state.presets.data) {
      state.presets.data = state.presets.data.filter(p => p.id !== action.payload);
    }
  });
};

export const getAllAnnotationJobsReducer = (
  builder: ActionReducerMapBuilder<AnnotatorsState>,
): void => {
  builder.addCase(getAllAnnotationJobs.pending, state => {
    state.jobs.loading = 'pending';
  });
  builder.addCase(getAllAnnotationJobs.fulfilled, (state, action) => {
    state.jobs.data = action.payload;
    state.jobs.loading = 'succeeded';
  });
  builder.addCase(getAllAnnotationJobs.rejected, (state, action) => {
    state.jobs.loading = 'failed';
    state.jobs.error = action.error as Error;
  });
};

export const cancelAnnotationJobReducer = (
  builder: ActionReducerMapBuilder<AnnotatorsState>,
): void => {
  builder.addCase(cancelAnnotationJob.fulfilled, (state, action) => {
    if (state.jobs.data) {
      const index = state.jobs.data.findIndex(j => j.id === action.payload.id);
      if (index !== MINUS_ONE) {
        state.jobs.data[index] = action.payload;
      }
    }
  });
};
